// src/components/subscription/Checkout.jsx

import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { loadStripe } from '@stripe/stripe-js';
import {
  PaymentElement,
  LinkAuthenticationElement,
  CardElement,
  Elements,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';

import { PLAN_CONFIGS } from './config/constants';
import { formatCurrency } from '../../../utils/currency';

import axios from 'axios';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  //   timeout: 5000,
  timeout: 30000, // Increase to 30 seconds
});

const token = localStorage.getItem('ownerToken');

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const PaymentMethodCard = ({
  method,
  isDefault,
  onDelete,
  onSetDefault,
  isLoading,
  isSelected,
  onSelect,
}) => {
  return (
    <div
      className={`border rounded-lg p-4 cursor-pointer ${
        isSelected ? 'border-blue-500 bg-blue-50' : 'border-gray-200'
      }`}
      onClick={onSelect}
    >
      <div className='flex items-center justify-between'>
        <div className='flex-1'>
          <p className='font-medium flex items-center gap-2'>
            {method.card.brand.toUpperCase()} •••• {method.card.last4}
            {isDefault && (
              <span className='inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800'>
                Default
              </span>
            )}
          </p>
          <p className='text-sm text-gray-600'>
            Expires {method.card.exp_month}/{method.card.exp_year}
          </p>
        </div>
        <div className='flex items-center gap-2'>
          {!isDefault && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onSetDefault(method.id);
              }}
              disabled={isLoading}
              className='px-3 py-1 text-sm text-blue-600 hover:text-blue-800 disabled:opacity-50'
            >
              Set as Default
            </button>
          )}
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDelete(method.id);
            }}
            // disabled={isLoading || isDefault}
            disabled={isLoading}
            className='px-3 py-1 text-sm text-red-600 hover:text-red-800 disabled:opacity-50'
            // title={
            //   isDefault
            //     ? 'Cannot delete default payment method'
            //     : 'Delete payment method'
            // }
            title={'Delete payment method'}
          >
            Delete
          </button>
          <input
            type='radio'
            checked={isSelected}
            onChange={() => onSelect()}
            className='h-4 w-4 text-blue-600'
          />
        </div>
      </div>
    </div>
  );
};

const CheckoutContent = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const stripe = useStripe();
  const elements = useElements();

  const abortController = useRef(new AbortController());

  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);

  const [attempt, setAttempt] = useState(0);

  const [defaultPaymentMethod, setDefaultPaymentMethod] = useState(null);
  const [setupIntent, setSetupIntent] = useState(null);

  const [paymentMethods, setPaymentMethods] = useState([]);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);
  const [showNewCardForm, setShowNewCardForm] = useState(false);
  //   const [setupIntent, setSetupIntent] = useState(null);

  const [deletingPaymentMethod, setDeletingPaymentMethod] = useState(false);
  const [updatingDefault, setUpdatingDefault] = useState(false);

  const [defaultPaymentMethodId, setDefaultPaymentMethodId] = useState(null);

  const { plan, currentPlan, isUpgrade, isDowngrade } = location.state || {};

  const [proratedAmount, setProratedAmount] = useState(null);
  const [isCalculating, setIsCalculating] = useState(true);
  const [prorationBreakdown, setProratedBreakdown] = useState(null);

  useEffect(() => {
    if (!plan) {
      navigate('/subscription/plans');
    }
  }, [plan, navigate]);

  //   useEffect(() => {
  //     fetchDefaultPaymentMethod();
  //   }, []);

  // useEffect(() => {
  //   fetchPaymentData();
  // }, []);

  useEffect(() => {
    if (isUpgrade) {
      calculateProration();
    }
    fetchPaymentData();
  }, [plan, currentPlan, isUpgrade]);

  const calculateProration = async () => {
    try {
      const token = localStorage.getItem('ownerToken');
      const response = await api.post(
        '/subscriptions/calculate-proration',
        {
          currentPlanId: currentPlan.planType,
          newPlanId: plan.id,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      setProratedAmount(response.data.proratedAmount);
      setProratedBreakdown(response.data.breakdown);
    } catch (error) {
      console.error('Error calculating proration:', error);
      toast.error('Failed to calculate proration amount');
    } finally {
      setIsCalculating(false);
    }
  };

  //   useEffect(() => {
  //     if (!plan) {
  //       navigate('/subscription/plans');
  //       return;
  //     }

  //     console.log('123123123');
  //     fetchPaymentData();

  //     // Cleanup function for interrupted transactions
  //     const handleBeforeUnload = (e) => {
  //       if (loading) {
  //         e.preventDefault();
  //         e.returnValue = '';
  //         abortController.current.abort();
  //       }
  //     };

  //     window.addEventListener('beforeunload', handleBeforeUnload);

  //     return () => {
  //       window.removeEventListener('beforeunload', handleBeforeUnload);
  //       abortController.current.abort();
  //     };
  //   }, [plan, navigate, loading]);

  const fetchPaymentData = async () => {
    try {
      const token = localStorage.getItem('ownerToken');
      const response = await api.get('/subscriptions/payment-methods', {
        headers: { Authorization: `Bearer ${token}` },
        // signal: abortController.current.signal,
      });

      setPaymentMethods(response.data.paymentMethods);
      setSetupIntent(response.data.setupIntent);

      console.log(response);

      // If there's a default payment method, select it
      // Get the default payment method from Stripe
      if (response.data.defaultPaymentMethod) {
        setDefaultPaymentMethodId(response.data.defaultPaymentMethod.id);
        // Pre-select the default payment method
        setSelectedPaymentMethod(response.data.defaultPaymentMethod);
      } else {
        console.log('asdf');
        setShowNewCardForm(true);
      }
    } catch (error) {
      console.error('Error fetching payment methods:', error);
      toast.error('Failed to load payment methods');
      if (!axios.isCancel(error)) {
        console.error('Error fetching payment methods:', error);
        toast.error('Failed to load payment methods');
      }
    }
  };

  const calculateTotal = () => {
    if (isDowngrade) return 0;
    const basePrice = plan.prices[currentPlan?.currency || 'usd'];
    // if (isUpgrade && currentPlan?.type !== 'free') {
    //   // Proration handled on the backend
    //   return basePrice;
    // }
    // return basePrice;
    if (isUpgrade && proratedAmount !== null) {
      return proratedAmount;
    }
    return basePrice;
  };

  //   const handleSubmit = async (e) => {
  //     e.preventDefault();
  //     if (!stripe || loading || (!selectedPaymentMethod && !showNewCardForm))
  //       return;

  //     setLoading(true);
  //     setError(null);

  //     try {
  //       let paymentMethodId;

  //       if (showNewCardForm) {
  //         // Handle new card setup
  //         const { error: submitError } = await elements.submit();
  //         if (submitError) {
  //           throw submitError;
  //         }

  //         const { error: setupError, setupIntent: confirmedSetup } =
  //           await stripe.confirmCardSetup(setupIntent, {
  //             payment_method: {
  //               card: elements.getElement(CardElement),
  //               billing_details: { email },
  //             },
  //           });

  //         if (setupError) {
  //           throw new Error(setupError.message);
  //         }

  //         paymentMethodId = confirmedSetup.payment_method;
  //       } else {
  //         // Use selected existing payment method
  //         paymentMethodId = selectedPaymentMethod.id;
  //       }

  //       const token = localStorage.getItem('ownerToken');
  //       const endpoint = isUpgrade
  //         ? '/subscriptions/upgrade'
  //         : isDowngrade
  //         ? '/subscriptions/downgrade'
  //         : '/subscriptions/create';

  //       console.log(endpoint);

  //       const response = await api.post(
  //         endpoint,
  //         {
  //           planId: plan.id,
  //           paymentMethodId,
  //           email,
  //           currency: currentPlan?.currency || 'usd',
  //         },
  //         {
  //           headers: { Authorization: `Bearer ${token}` },
  //         }
  //       );

  //       // If we get here, the subscription was created successfully
  //       toast.success('Subscription processed successfully!');
  //       navigate('/owner/test/membershipplans');
  //     } catch (error) {
  //       console.error('Error processing subscription:', error);
  //       setError(error.message);
  //       toast.error('Failed to process subscription');
  //     } finally {
  //       setLoading(false);
  //     }
  //   };

  //   const handleSubmit = async (e) => {
  //     e.preventDefault();
  //     if (!stripe || loading || (!selectedPaymentMethod && !showNewCardForm))
  //       return;

  //     setLoading(true);
  //     setError(null);

  //     const retryCount = 3; // Number of retry attempts
  //     let lastError;

  //     for (let attempt = 0; attempt < retryCount; attempt++) {
  //       try {
  //         let paymentMethodId;

  //         if (showNewCardForm) {
  //           const { error: submitError } = await elements.submit();
  //           if (submitError) {
  //             throw submitError;
  //           }

  //           const { error: setupError, setupIntent: confirmedSetup } =
  //             await stripe.confirmCardSetup(setupIntent, {
  //               payment_method: {
  //                 card: elements.getElement(CardElement),
  //                 billing_details: { email },
  //               },
  //             });

  //           if (setupError) {
  //             throw new Error(setupError.message);
  //           }

  //           paymentMethodId = confirmedSetup.payment_method;
  //         } else {
  //           paymentMethodId = selectedPaymentMethod.id;
  //         }

  //         const token = localStorage.getItem('ownerToken');
  //         const endpoint = isUpgrade
  //           ? '/subscriptions/upgrade'
  //           : isDowngrade
  //           ? '/subscriptions/downgrade'
  //           : '/subscriptions/create';

  //         const response = await api.post(
  //           endpoint,
  //           {
  //             planId: plan.id,
  //             paymentMethodId,
  //             email,
  //             currency: currentPlan?.currency || 'usd',
  //           },
  //           {
  //             headers: { Authorization: `Bearer ${token}` },
  //             timeout: 30000, // Override timeout for this specific request
  //           }
  //         );

  //         // If we get here, the request was successful
  //         toast.success('Subscription processed successfully!');
  //         navigate('/owner/test/membershipplans');
  //         return;
  //       } catch (error) {
  //         lastError = error;
  //         console.error(`Attempt ${attempt + 1} failed:`, error);

  //         // Only retry on timeout errors
  //         if (error.code !== 'ECONNABORTED') {
  //           break;
  //         }

  //         // Wait before retrying (exponential backoff)
  //         await new Promise((resolve) =>
  //           setTimeout(resolve, Math.pow(2, attempt) * 1000)
  //         );
  //       }
  //     }

  //     // If we get here, all attempts failed
  //     console.error('Error processing subscription:', lastError);
  //     setError(lastError.message || 'Failed to process subscription');
  //     toast.error('Failed to process subscription');
  //     setLoading(false);
  //   };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || loading || (!selectedPaymentMethod && !showNewCardForm))
      return;

    setLoading(true);
    setError(null);
    setAttempt((prev) => prev + 1);

    try {
      let paymentMethodId;

      if (showNewCardForm) {
        const { error: submitError } = await elements.submit();
        if (submitError) {
          throw submitError;
        }

        const { error: setupError, setupIntent: confirmedSetup } =
          await stripe.confirmCardSetup(setupIntent, {
            payment_method: {
              card: elements.getElement(CardElement),
              billing_details: { email },
            },
          });

        if (setupError) {
          throw new Error(setupError.message);
        }

        paymentMethodId = confirmedSetup.payment_method;
      } else {
        paymentMethodId = selectedPaymentMethod.id;
      }

      const token = localStorage.getItem('ownerToken');
      const endpoint = isUpgrade
        ? '/subscriptions/upgrade'
        : isDowngrade
        ? '/subscriptions/downgrade'
        : '/subscriptions/create';

      console.log(endpoint);
      const response = await api.post(
        endpoint,
        {
          planId: plan.id,
          paymentMethodId,
          email,
          currency: currentPlan?.currency || 'usd',
        },
        {
          headers: { Authorization: `Bearer ${token}` },
          signal: abortController.current.signal,
        }
      );

      toast.success('Subscription processed successfully!');
      navigate('/owner/test/membership');
    } catch (error) {
      if (axios.isCancel(error)) {
        console.log('Request cancelled');
        return;
      }

      console.error('Error processing subscription:', error);
      setError(error.message || 'Failed to process subscription');
      toast.error('Failed to process subscription');

      // Cleanup on failure
      try {
        const token = localStorage.getItem('ownerToken');
        await api.post(
          '/subscriptions/cleanup',
          {
            planId: plan.id,
          },
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
      } catch (cleanupError) {
        console.error('Cleanup error:', cleanupError);
      }
    } finally {
      setLoading(false);
    }
  };

  //   const handleSubmit = async (e) => {
  //     e.preventDefault();
  //     if (!stripe || !elements || loading) return;

  //     setLoading(true);
  //     setError(null);
  //     // setMessage(null);

  //     try {
  //       const { error: submitError } = await elements.submit();
  //       if (submitError) {
  //         throw submitError;
  //       }

  //       let paymentMethodId = defaultPaymentMethod;

  //       //   if (!defaultPaymentMethod) {
  //       //     // Create new payment method
  //       //     const { error: setupError, setupIntent: confirmedSetup } =
  //       //       await stripe.confirmCardSetup(setupIntent, {
  //       //         payment_method: {
  //       //           card: elements.getElement(CardElement),
  //       //           billing_details: { email },
  //       //         },
  //       //       });

  //       if (!defaultPaymentMethod) {
  //         // Create new payment method
  //         const { error: setupError, setupIntent: confirmedSetup } =
  //           await stripe.confirmCardSetup(setupIntent, {
  //             payment_method: {
  //               card: elements.getElement(CardElement),
  //               billing_details: { email },
  //             },
  //           });

  //         if (setupError) {
  //           throw new Error(setupError.message);
  //         }

  //         paymentMethodId = confirmedSetup.payment_method;
  //       }

  //       const endpoint = isUpgrade
  //         ? '/subscriptions/upgrade'
  //         : isDowngrade
  //         ? '/subscriptions/downgrade'
  //         : '/subscriptions/create';

  //       const response = await api.post(
  //         // endpoint,
  //         // {
  //         //   planId: plan.id,
  //         //   email,
  //         //   currency: currentPlan?.currency || 'usd',
  //         // },
  //         endpoint,
  //         {
  //           planId: plan.id,
  //           paymentMethodId: paymentMethodId.id,
  //           email,
  //           currency: currentPlan?.currency || 'usd',
  //         },
  //         {
  //           headers: { Authorization: `Bearer ${token}` },
  //         }
  //       );

  //       //   const { clientSecret } = response.data;

  //       //   const { error: confirmError } = await stripe.confirmPayment({
  //       //     elements,
  //       //     clientSecret,
  //       //     confirmParams: {
  //       //       return_url: `${window.location.origin}/subscription/success`,
  //       //       payment_method_data: {
  //       //         billing_details: { email },
  //       //       },
  //       //     },
  //       //   });

  //       //   if (confirmError) {
  //       //     throw confirmError;
  //       //   }

  //       const { requiresAction, clientSecret } = response.data;

  //       console.log(response);
  //       if (requiresAction) {
  //         // Confirm the payment intent
  //         const { error } = await stripe.confirmCardPayment(clientSecret);
  //         if (error) {
  //           throw new Error('Authentication failed. Please try again.');
  //         }
  //       }

  //       toast.success('Subscription created successfully!');
  //       navigate('/owner/test/membershipplans');

  //       // If we get here, user will be redirected by stripe.confirmPayment
  //     } catch (error) {
  //       console.error('Error processing subscription:', error);
  //       setError(error.message);
  //       toast.error('Failed to process subscription');
  //       setLoading(false);
  //     }
  //   };

  //   const fetchDefaultPaymentMethod = async () => {
  //     try {
  //       const response = await api.get('/subscriptions/payment-methods', {
  //         headers: { Authorization: `Bearer ${token}` },
  //       });
  //       console.log(response);
  //       //   setDefaultPaymentMethod(response.data.defaultMethod);
  //       setDefaultPaymentMethod(response.data.paymentMethod);
  //       setSetupIntent(response.data.clientSecret);
  //     } catch (error) {
  //       console.error('Error fetching default payment method:', error);
  //     }
  //   };

  const handleDeletePaymentMethod = async (methodId) => {
    // if (!confirm('Are you sure you want to delete this payment method?')) {
    //   return;
    // }

    setDeletingPaymentMethod(true);
    try {
      const token = localStorage.getItem('ownerToken');
      await api.delete(`/subscriptions/payment-methods/${methodId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      await fetchPaymentData();
      toast.success('Payment method deleted successfully');
    } catch (error) {
      console.error('Error deleting payment method:', error);
      toast.error(
        error.response?.data?.error || 'Failed to delete payment method'
      );
    } finally {
      setDeletingPaymentMethod(false);
    }
  };

  const handleSetDefaultPaymentMethod = async (methodId) => {
    setUpdatingDefault(true);
    try {
      const token = localStorage.getItem('ownerToken');
      await api.put(
        `/subscriptions/payment-methods/${methodId}`,
        {
          isDefault: true,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      await fetchPaymentData();
      toast.success('Default payment method updated');
    } catch (error) {
      console.error('Error updating default payment method:', error);
      toast.error(
        error.response?.data?.error || 'Failed to update default payment method'
      );
    } finally {
      setUpdatingDefault(false);
    }
  };

  if (!plan) return null;

  return (
    <div className='max-w-2xl mx-auto p-8'>
      <h2 className='text-2xl font-bold mb-6'>
        {isUpgrade
          ? 'Upgrade Subscription'
          : isDowngrade
          ? 'Change Subscription Plan'
          : 'Complete Purchase'}
      </h2>

      {/* Payment Summary */}
      <div className='bg-gray-50 rounded-lg p-6 mb-8'>
        <h3 className='text-lg font-semibold mb-4'>Payment Summary</h3>
        <div className='space-y-2'>
          <div className='flex justify-between'>
            <span>{plan.name}</span>
            <span>
              {formatCurrency(
                plan.prices[currentPlan?.currency || 'usd'] / 100
              )}
              /month
            </span>
          </div>

          {currentPlan && currentPlan.type !== 'free' && (
            <div className='flex justify-between text-gray-600'>
              <span>Current Plan</span>
              <span>{PLAN_CONFIGS[currentPlan.planType].name}</span>
            </div>
          )}

          {/* {isUpgrade && (
            <div className='flex justify-between text-gray-600'>
              <span>Proration Credit</span>
              {isCalculating ? (
                <span>Calculating...</span>
              ) : (
                <span>-{formatCurrency(proratedAmount / 100)}</span>
              )}
            </div>
          )} */}

          {/* {isDowngrade && (
            <div className='bg-yellow-50 p-4 rounded-md mt-4'>
              <p className='text-sm text-yellow-800'>
                Your plan will change to {plan.name} at the end of your current
                billing period (
                {currentPlan?.currentPeriodEnd
                  ? new Date(currentPlan.currentPeriodEnd).toLocaleDateString()
                  : 'N/A'}
                ). No immediate payment is required.
              </p>
            </div>
          )}

          <div className='border-t pt-2 mt-2'>
            <div className='flex justify-between font-semibold'>
              <span>Total due today</span>
              <span>{formatCurrency(calculateTotal() / 100)}</span>
            </div>
          </div> */}

          {/* {isDowngrade ? (
            <div className='bg-yellow-50 p-4 rounded-md mt-4'>
              <p className='text-sm text-yellow-800'>
                Your plan will change to {plan.name} at the end of your current
                billing period (
                {currentPlan?.currentPeriodEnd
                  ? new Date(currentPlan.currentPeriodEnd).toLocaleDateString()
                  : 'N/A'}
                ). No immediate payment is required.
              </p>
            </div>
          ) : (
            <div className='border-t pt-2 mt-2'>
              <div className='flex justify-between font-semibold'>
                <span>Total due today</span>
                <span>
                  {isCalculating
                    ? 'Calculating...'
                    : formatCurrency(calculateTotal() / 100)}
                </span>
              </div>
            </div>
          )} */}

          {isUpgrade && (
            <>
              {isCalculating ? (
                <div className='text-center py-2'>Calculating proration...</div>
              ) : (
                <>
                  {prorationBreakdown && (
                    <div className='border-t border-gray-200 pt-2 space-y-1'>
                      <div className='flex justify-between text-gray-600'>
                        <span>New Plan Price</span>
                        <span>
                          {formatCurrency(
                            prorationBreakdown.newPlanPrice / 100
                          )}
                        </span>
                      </div>
                      <div className='flex justify-between text-gray-600'>
                        <span>Unused Time Credit</span>
                        <span>
                          -
                          {formatCurrency(prorationBreakdown.creditValue / 100)}
                        </span>
                      </div>
                    </div>
                  )}
                </>
              )}
            </>
          )}

          {/* Final Amount */}
          {isDowngrade ? (
            <div className='bg-yellow-50 p-4 rounded-md mt-4'>
              <p className='text-sm text-yellow-800'>
                Your plan will change to {plan.name} at the end of your current
                billing period (
                {currentPlan?.currentPeriodEnd
                  ? new Date(currentPlan.currentPeriodEnd).toLocaleDateString()
                  : 'N/A'}
                ). No immediate payment is required.
              </p>
            </div>
          ) : (
            <div className='border-t pt-2 mt-2'>
              <div className='flex justify-between font-semibold'>
                <span>Total due today</span>
                <span>
                  {isCalculating
                    ? 'Calculating...'
                    : formatCurrency(calculateTotal() / 100)}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* {defaultPaymentMethod && (
        <div className='mb-6'>
          <h3 className='text-lg font-semibold mb-4'>Current Payment Method</h3>
          <div className='bg-blue-50 border border-blue-200 rounded-lg p-4'>
            <div className='flex justify-between items-center'>
              <div>
                <p className='font-medium'>
                  {defaultPaymentMethod.card.brand.toUpperCase()} ••••{' '}
                  {defaultPaymentMethod.card.last4}
                </p>
                <p className='text-sm text-gray-600'>
                  Expires {defaultPaymentMethod.card.exp_month}/
                  {defaultPaymentMethod.card.exp_year}
                </p>
                <p className='text-sm text-blue-600 mt-1'>
                  Used for recurring payments
                </p>
              </div>
              <button
                // onClick={() => setShowUpdateDialog(true)}
                className='text-blue-600 hover:text-blue-800'
              >
                Update
              </button>
            </div>
          </div>
        </div>
      )} */}

      {/* Payment Form */}
      {/*       
      <form onSubmit={handleSubmit} className='space-y-6'>
        <LinkAuthenticationElement onChange={(e) => setEmail(e.value.email)} />

        <PaymentElement />

        {error && <div className='text-red-500 text-sm mt-4'>{error}</div>}

        {message && (
          <div className='text-green-500 text-sm mt-4'>{message}</div>
        )}

        <button
          type='submit'
          disabled={loading || !stripe || !elements}
          className='w-full bg-blue-500 text-white py-3 px-6 rounded-lg hover:bg-blue-600 
                   disabled:opacity-50 disabled:cursor-not-allowed'
        >
          {loading ? (
            <div className='flex items-center justify-center'>
              Processing...
            </div>
          ) : isDowngrade ? (
            'Confirm Plan Change'
          ) : (
            `Pay ${formatCurrency(calculateTotal() / 100)}`
          )}
        </button>

        <p className='text-center text-sm text-gray-600 mt-4'>
          {isDowngrade
            ? 'Your plan will change at the end of your current billing period'
            : 'Your subscription will start immediately after successful payment'}
        </p>
      </form> */}
      <form onSubmit={handleSubmit} className='space-y-6'>
        {paymentMethods.length > 0 && (
          <div className='mb-8'>
            <h3 className='text-lg font-semibold mb-4'>Payment Method</h3>
            <div className='space-y-4'>
              {paymentMethods.map((method) => (
                // <PaymentMethodCard
                //   key={method.id}
                //   method={method}
                //   isDefault={defaultPaymentMethod?.id === method.id}
                //   isSelected={selectedPaymentMethod?.id === method.id}
                //   onSelect={() => {
                //     setSelectedPaymentMethod(method);
                //     setShowNewCardForm(false);
                //   }}
                //   onDelete={handleDeletePaymentMethod}
                //   onSetDefault={handleSetDefaultPaymentMethod}
                //   isLoading={deletingPaymentMethod || updatingDefault}
                // />
                <PaymentMethodCard
                  key={method.id}
                  method={method}
                  isDefault={method.id === defaultPaymentMethodId}
                  isSelected={selectedPaymentMethod?.id === method.id}
                  onSelect={() => {
                    setSelectedPaymentMethod(method);
                    setShowNewCardForm(false);
                  }}
                  onDelete={handleDeletePaymentMethod}
                  onSetDefault={handleSetDefaultPaymentMethod}
                  isLoading={deletingPaymentMethod || updatingDefault}
                />
              ))}

              <button
                type='button'
                className={`w-full border rounded-lg p-4 text-left hover:border-blue-300 transition-colors
                   ${
                     showNewCardForm
                       ? 'border-blue-500 bg-blue-50'
                       : 'border-gray-200'
                   }`}
                onClick={() => {
                  setShowNewCardForm(true);
                  setSelectedPaymentMethod(null);
                }}
              >
                <div className='flex items-center justify-between'>
                  <span className='flex items-center'>
                    <svg
                      className='w-5 h-5 mr-2'
                      fill='none'
                      stroke='currentColor'
                      viewBox='0 0 24 24'
                    >
                      <path
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        strokeWidth={2}
                        d='M12 6v6m0 0v6m0-6h6m-6 0H6'
                      />
                    </svg>
                    Add new card
                  </span>
                  <input
                    type='radio'
                    checked={showNewCardForm}
                    onChange={() => {}}
                    className='h-4 w-4 text-blue-600'
                  />
                </div>
              </button>
            </div>
          </div>
          //   <div className='mb-8'>
          //     <h3 className='text-lg font-semibold mb-4'>
          //       Select Payment Method
          //     </h3>
          //     <div className='space-y-4'>
          //       {paymentMethods.map((method) => (
          //         <div
          //           key={method.id}
          //           className={`border rounded-lg p-4 cursor-pointer ${
          //             selectedPaymentMethod?.id === method.id
          //               ? 'border-blue-500 bg-blue-50'
          //               : ''
          //           }`}
          //           onClick={() => {
          //             setSelectedPaymentMethod(method);
          //             setShowNewCardForm(false);
          //           }}
          //         >
          //           <div className='flex items-center justify-between'>
          //             <div>
          //               <p className='font-medium'>
          //                 {method.card.brand.toUpperCase()} ••••{' '}
          //                 {method.card.last4}
          //               </p>
          //               <p className='text-sm text-gray-600'>
          //                 Expires {method.card.exp_month}/{method.card.exp_year}
          //               </p>
          //             </div>
          //             <input
          //               type='radio'
          //               checked={selectedPaymentMethod?.id === method.id}
          //               onChange={() => {}}
          //               className='h-4 w-4 text-blue-600'
          //             />
          //           </div>
          //         </div>
          //       ))}

          //       <button
          //         type='button'
          //         className={`w-full border rounded-lg p-4 text-left ${
          //           showNewCardForm ? 'border-blue-500 bg-blue-50' : ''
          //         }`}
          //         onClick={() => {
          //           setShowNewCardForm(true);
          //           setSelectedPaymentMethod(null);
          //         }}
          //       >
          //         + Add new card
          //       </button>
          //     </div>
          //   </div>
        )}

        {/* New Card Form */}
        {showNewCardForm && (
          <div className='mb-8'>
            <h3 className='text-lg font-semibold mb-4'>Add New Card</h3>
            <CardElement
              options={{
                style: {
                  base: {
                    fontSize: '16px',
                    color: '#424770',
                    '::placeholder': {
                      color: '#aab7c4',
                    },
                  },
                },
              }}
            />
          </div>
        )}

        {error && <div className='text-red-500 text-sm mt-4'>{error}</div>}
        {message && (
          <div className='text-green-500 text-sm mt-4'>{message}</div>
        )}

        <button
          type='submit'
          disabled={loading || (!selectedPaymentMethod && !showNewCardForm)}
          className='w-full bg-blue-500 text-white py-3 px-6 rounded-lg hover:bg-blue-600 
                   disabled:opacity-50 disabled:cursor-not-allowed'
        >
          {loading ? (
            <div className='flex items-center justify-center'>
              <div className='animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2' />
              Processing...
            </div>
          ) : isDowngrade ? (
            'Confirm Plan Change'
          ) : (
            `Pay ${formatCurrency(calculateTotal() / 100)}`
          )}
        </button>

        <p className='text-center text-sm text-gray-600 mt-4'>
          {isDowngrade
            ? 'Your plan will change at the end of your current billing period'
            : 'Your subscription will start immediately after successful payment'}
        </p>
      </form>
    </div>
  );
};

const Checkout = () => {
  const [clientSecret, setClientSecret] = useState(null);
  const location = useLocation();
  const { plan, isUpgrade, isDowngrade } = location.state || {};

  useEffect(() => {
    const initializeCheckout = async () => {
      try {
        // Get setup intent for the specific plan and scenario
        const response = await api.post(
          '/subscriptions/payment-methods/setup-intent',
          {
            planId: plan?.id,
            isUpgrade,
            isDowngrade,
          },
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        setClientSecret(response.data.clientSecret);
      } catch (error) {
        console.error('Error initializing checkout:', error);
        toast.error('Failed to initialize checkout');
      }
    };

    if (plan) {
      console.log('initialize checkout');
      initializeCheckout();
    }
  }, [plan, isUpgrade, isDowngrade]);

  if (!clientSecret) {
    return (
      <div className='flex justify-center items-center min-h-screen'>
        <div className='animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500' />
      </div>
    );
  }

  return (
    <Elements
      stripe={stripePromise}
      options={{
        clientSecret,
        appearance: {
          theme: 'stripe',
          variables: {
            colorPrimary: '#3b82f6', // blue-500
            colorBackground: '#ffffff',
            colorText: '#1f2937', // gray-800
          },
        },
      }}
    >
      <CheckoutContent />
    </Elements>
  );
};

export default Checkout;

// // src/components/subscription/Checkout.jsx

// import React, { useState, useEffect } from 'react';
// import { useLocation, useNavigate } from 'react-router-dom';
// import { toast } from 'react-toastify';
// import { loadStripe } from '@stripe/stripe-js';
// import {
//   PaymentElement,
//   LinkAuthenticationElement,
//   CardElement,
//   Elements,
//   useStripe,
//   useElements,
// } from '@stripe/react-stripe-js';

// import { PLAN_CONFIGS } from './config/constants';
// import { formatCurrency } from '../../../utils/currency';

// import axios from 'axios';

// const api = axios.create({
//   baseURL: process.env.REACT_APP_API_URL,
//   timeout: 5000,
// });

// const token = localStorage.getItem('ownerToken');

// const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

// const CheckoutContent = () => {
//   const location = useLocation();
//   const navigate = useNavigate();
//   const stripe = useStripe();
//   const elements = useElements();

//   const [email, setEmail] = useState('');
//   const [loading, setLoading] = useState(false);
//   const [error, setError] = useState(null);
//   const [setupIntent, setSetupIntent] = useState(null);
//   const [paymentMethod, setPaymentMethod] = useState('card');
//   const [savedMethods, setSavedMethods] = useState([]);
//   const [selectedSavedMethod, setSelectedSavedMethod] = useState(null);

//   const { plan, currentPlan, isUpgrade, isDowngrade } = location.state || {};

//   useEffect(() => {
//     if (!plan) {
//       navigate('/subscription/plans');
//       return;
//     }
//     fetchInitialData();
//   }, [plan, navigate]);

//   const fetchInitialData = async () => {
//     try {
//       const methodsResponse = await api.get('/subscriptions/payment-methods', {
//         headers: { Authorization: `Bearer ${token}` },
//       });
//       setSavedMethods(methodsResponse.data.methods);

//       const setupResponse = await api.post(
//         '/subscriptions/payment-methods/setup-intent',
//         {},
//         {
//           headers: { Authorization: `Bearer ${token}` },
//         }
//       );
//       setSetupIntent(setupResponse.data.clientSecret);
//     } catch (error) {
//       console.error('Error initializing checkout:', error);
//       toast.error('Failed to initialize checkout');
//     }
//   };

//   const calculateTotal = () => {
//     if (isDowngrade) {
//       return 0; // Downgrades take effect at end of billing period
//     }
//     const basePrice = plan.prices[currentPlan?.currency || 'usd'];
//     if (isUpgrade && currentPlan) {
//       // If upgrade, calculate prorated amount...
//       // Implementation depends on your proration logic
//       return basePrice;
//     }
//     return basePrice;
//   };

//   const handleSubmit = async (e) => {
//     e.preventDefault();
//     if (!stripe || !elements || loading) return;

//     setLoading(true);
//     setError(null);

//     try {
//       let paymentMethodId = selectedSavedMethod;

//       if (!selectedSavedMethod) {
//         // Create new payment method
//         const { error: setupError, setupIntent: confirmedSetup } =
//           await stripe.confirmCardSetup(setupIntent, {
//             payment_method: {
//               card: elements.getElement(CardElement),
//               billing_details: { email },
//             },
//           });

//         if (setupError) {
//           throw new Error(setupError.message);
//         }

//         paymentMethodId = confirmedSetup.payment_method;
//       }

//       // Create or update subscription based on scenario
//       const endpoint = isUpgrade
//         ? '/subscriptions/upgrade'
//         : isDowngrade
//         ? '/subscriptions/downgrade'
//         : '/subscriptions/create';

//       const response = await api.post(
//         endpoint,
//         {
//           planId: plan.id,
//           paymentMethodId,
//           email,
//         },
//         {
//           headers: { Authorization: `Bearer ${token}` },
//         }
//       );

//       if (isDowngrade) {
//         toast.success(
//           `Your plan will be changed to ${plan.name} at the end of your billing period`
//         );
//       } else {
//         toast.success(
//           `Successfully ${isUpgrade ? 'upgraded' : 'subscribed to'} ${
//             plan.name
//           }`
//         );
//       }
//       navigate('/subscription/info');
//     } catch (error) {
//       console.error('Error processing subscription:', error);
//       setError(error.message);
//       toast.error('Failed to process subscription');
//     } finally {
//       setLoading(false);
//     }
//   };

//   if (!plan) return null;

//   return (
//     <div className='max-w-2xl mx-auto p-8'>
//       <h2 className='text-2xl font-bold mb-6'>
//         {isUpgrade
//           ? 'Upgrade Subscription'
//           : isDowngrade
//           ? 'Change Subscription Plan'
//           : 'Complete Purchase'}
//       </h2>

//       {/* Payment Summary */}
//       <div className='bg-gray-50 rounded-lg p-6 mb-8'>
//         <h3 className='text-lg font-semibold mb-4'>Payment Summary</h3>
//         <div className='space-y-2'>
//           <div className='flex justify-between'>
//             <span>{plan.name}</span>
//             <span>
//               {formatCurrency(
//                 plan.prices[currentPlan?.currency || 'usd'] / 100
//               )}
//               /month
//             </span>
//           </div>

//           {currentPlan && currentPlan.type !== 'free' && (
//             <div className='flex justify-between text-gray-600'>
//               <span>Current Plan</span>
//               <span>{PLAN_CONFIGS[currentPlan.planType].name}</span>
//             </div>
//           )}

//           {isDowngrade && (
//             <div className='bg-yellow-50 p-4 rounded-md mt-4'>
//               <p className='text-sm text-yellow-800'>
//                 Your plan will change to {plan.name} at the end of your current
//                 billing period (
//                 {currentPlan?.currentPeriodEnd
//                   ? new Date(currentPlan.currentPeriodEnd).toLocaleDateString()
//                   : 'N/A'}
//                 ). No immediate payment is required.
//               </p>
//             </div>
//           )}

//           <div className='border-t pt-2 mt-2'>
//             <div className='flex justify-between font-semibold'>
//               <span>Total due today</span>
//               <span>{formatCurrency(calculateTotal() / 100)}</span>
//             </div>
//           </div>
//         </div>
//       </div>

//       {/* Payment Method */}
//       <form onSubmit={handleSubmit} className='space-y-6'>
//         {/* Save Email for Notifications */}
//         <LinkAuthenticationElement onChange={(e) => setEmail(e.value.email)} />

//         {/* Saved Payment Methods */}
//         {savedMethods.length > 0 && (
//           <div className='mb-6'>
//             <h3 className='text-lg font-semibold mb-4'>
//               Saved Payment Methods
//             </h3>
//             <div className='space-y-4'>
//               {savedMethods.map((method) => (
//                 <div
//                   key={method.id}
//                   className={`border rounded-lg p-4 cursor-pointer ${
//                     selectedSavedMethod === method.id
//                       ? 'border-blue-500 bg-blue-50'
//                       : ''
//                   }`}
//                   onClick={() => setSelectedSavedMethod(method.id)}
//                 >
//                   <div className='flex justify-between items-center'>
//                     <div>
//                       <p className='font-medium'>
//                         {method.card.brand.toUpperCase()} ••••{' '}
//                         {method.card.last4}
//                       </p>
//                       <p className='text-sm text-gray-600'>
//                         Expires {method.card.exp_month}/{method.card.exp_year}
//                       </p>
//                     </div>
//                   </div>
//                 </div>
//               ))}
//             </div>
//           </div>
//         )}

//         {/* New Card Input */}
//         <div>
//           <h3 className='text-lg font-semibold mb-4'>
//             {savedMethods.length > 0 ? 'Or Pay with New Card' : 'Card Details'}
//           </h3>
//           <div className='border rounded-lg p-4'>
//             <CardElement
//               options={{
//                 style: {
//                   base: {
//                     fontSize: '16px',
//                     color: '#424770',
//                     '::placeholder': {
//                       color: '#aab7c4',
//                     },
//                   },
//                   invalid: {
//                     color: '#9e2146',
//                   },
//                 },
//               }}
//               onChange={() => setSelectedSavedMethod(null)}
//             />
//           </div>
//         </div>

//         {error && <div className='text-red-500 text-sm mt-4'>{error}</div>}

//         <button
//           type='submit'
//           disabled={loading || !stripe || !elements}
//           className='w-full bg-blue-500 text-white py-3 px-6 rounded-lg hover:bg-blue-600
//                    disabled:opacity-50 disabled:cursor-not-allowed'
//         >
//           {loading ? (
//             <div className='flex items-center justify-center'>
//               {/* <LoadingSpinner className="h-5 w-5 mr-2" /> */}
//               Processing...
//             </div>
//           ) : isDowngrade ? (
//             'Confirm Plan Change'
//           ) : (
//             `Pay ${formatCurrency(calculateTotal() / 100)}`
//           )}
//         </button>

//         <p className='text-center text-sm text-gray-600 mt-4'>
//           {isDowngrade
//             ? 'Your plan will change at the end of your current billing period'
//             : 'Your subscription will start immediately after successful payment'}
//         </p>
//       </form>
//     </div>
//   );
// };

// const Checkout = () => {
//   return (
//     <Elements stripe={stripePromise}>
//       <CheckoutContent />
//     </Elements>
//   );
// };

// export default Checkout;

// import React, { useEffect, useState } from 'react';
// import { useStripe, useElements, CardElement } from '@stripe/react-stripe-js';
// import { useSearchParams } from 'react-router-dom';
// import axios from 'axios';

// const CheckoutPage = () => {
//   const stripe = useStripe();
//   const elements = useElements();
//   const [searchParams] = useSearchParams();
//   const planId = searchParams.get('planId');
//   const action = searchParams.get('action');
//   const [loading, setLoading] = useState(false);
//   const [error, setError] = useState(null);
//   const [success, setSuccess] = useState(false);
//   const [currentPaymentMethod, setCurrentPaymentMethod] = useState(null);

//   const token = localStorage.getItem('ownerToken');

//   //   const { plan, proratedAmount, isUpgrade, currentPlan, isDowngrade } =
//   //     location.state || {};

//   // Fetch current payment method
//   useEffect(() => {
//     const fetchCurrentPaymentMethod = async () => {
//       try {
//         const response = await axios.get('/api/subscriptions/payment-methods', {
//           headers: { Authorization: `Bearer ${token}` },
//         });
//         setCurrentPaymentMethod(response.data.paymentMethod);
//       } catch (err) {
//         console.error('Error fetching payment method:', err);
//       }
//     };

//     fetchCurrentPaymentMethod();
//   }, []);

//   // Submit form for subscription creation, upgrade, or downgrade
//   const handleSubmit = async (e) => {
//     e.preventDefault();
//     setLoading(true);
//     setError(null);

//     if (!stripe || !elements) {
//       setError('Stripe is not loaded.');
//       setLoading(false);
//       return;
//     }

//     const cardElement = elements.getElement(CardElement);

//     try {
//       const { paymentMethod, error: paymentMethodError } =
//         await stripe.createPaymentMethod({
//           type: 'card',
//           card: cardElement,
//         });

//       if (paymentMethodError) {
//         setError(paymentMethodError.message);
//         setLoading(false);
//         return;
//       }

//       console.log(action);

//       const response = await axios.post(
//         `/api/subscriptions/${action}`,
//         {
//           planId,

//           paymentMethodId: paymentMethod.id,
//         },
//         {
//           headers: { Authorization: `Bearer ${token}` },
//         }
//       );

//       setSuccess(true);
//     } catch (err) {
//       setError(err.response?.data?.error || 'An error occurred.');
//     }

//     setLoading(false);
//   };

//   // Update current payment method
//   const handlePaymentMethodUpdate = async () => {
//     setLoading(true);
//     setError(null);

//     if (!stripe || !elements) {
//       setError('Stripe is not loaded.');
//       setLoading(false);
//       return;
//     }

//     const cardElement = elements.getElement(CardElement);

//     try {
//       const { paymentMethod, error: paymentMethodError } =
//         await stripe.createPaymentMethod({
//           type: 'card',
//           card: cardElement,
//         });

//       if (paymentMethodError) {
//         setError(paymentMethodError.message);
//         setLoading(false);
//         return;
//       }

//       await axios.post('/api/subscriptions/payment-method', {
//         paymentMethodId: paymentMethod.id,
//       });

//       setCurrentPaymentMethod({
//         id: paymentMethod.id,
//         card: paymentMethod.card,
//       });

//       setSuccess(true);
//     } catch (err) {
//       setError(err.response?.data?.error || 'An error occurred.');
//     }

//     setLoading(false);
//   };

//   return (
//     <div>
//       <h1>
//         {action === 'upgrade'
//           ? 'Upgrade Plan'
//           : action === 'downgrade'
//           ? 'Downgrade Plan'
//           : 'Subscribe to Plan'}
//       </h1>

//       {currentPaymentMethod && (
//         <div>
//           <h2>Current Payment Method</h2>
//           <p>
//             Card: {currentPaymentMethod.card.brand} ****{' '}
//             {currentPaymentMethod.card.last4}
//             <br />
//             Expires: {currentPaymentMethod.card.exp_month}/
//             {currentPaymentMethod.card.exp_year}
//           </p>
//         </div>
//       )}

//       <form onSubmit={handleSubmit}>
//         <h2>
//           {currentPaymentMethod
//             ? 'Enter New Card Details (Optional)'
//             : 'Enter Card Details'}
//         </h2>
//         <CardElement />
//         <button type='submit' disabled={loading}>
//           {loading
//             ? 'Processing...'
//             : action === 'upgrade'
//             ? 'Upgrade Plan'
//             : 'Submit'}
//         </button>
//       </form>

//       <button onClick={handlePaymentMethodUpdate} disabled={loading}>
//         {loading ? 'Updating...' : 'Update Payment Method'}
//       </button>

//       {error && <p style={{ color: 'red' }}>{error}</p>}
//       {success && <p style={{ color: 'green' }}>Payment successful!</p>}
//     </div>
//   );
// };

// export default CheckoutPage;

// // src/components/subscription/Checkout.jsx
// import React, { useState, useEffect } from 'react';
// import { useLocation, useNavigate } from 'react-router-dom';
// import { toast } from 'react-toastify';
// import {
//   PaymentElement,
//   LinkAuthenticationElement,
//   CardElement,
//   useStripe,
//   useElements,
// } from '@stripe/react-stripe-js';
// // import api from '../../utils/api';
// // import { getAuthHeader } from '../../utils/authToken';
// // import { PLAN_CONFIGS } from '../../config/constants';
// import { PLAN_CONFIGS } from './config/constants';

// // import { formatCurrency } from '../../utils/currency';
// import { formatCurrency } from '../../../utils/currency';

// // import LoadingSpinner from '../common/LoadingSpinner';

// // // src/components/subscription/Checkout.jsx

// // import {
// //   Elements,
// //   PaymentElement,
// //   LinkAuthenticationElement,
// //   useStripe,
// //   useElements,
// // } from '@stripe/react-stripe-js';
// // import { loadStripe } from '@stripe/stripe-js';
// // import { PaymentMethodDialog } from './PaymentMethodDialog';
// // import { formatCurrency } from '../../../utils/currency';
// import axios from 'axios';

// const api = axios.create({
//   baseURL: process.env.REACT_APP_API_URL,
//   timeout: 5000,
// });

// const token = localStorage.getItem('ownerToken');

// // const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

// const Checkout = () => {
//   const location = useLocation();
//   const navigate = useNavigate();
//   const stripe = useStripe();
//   const elements = useElements();

//   const [email, setEmail] = useState('');
//   const [loading, setLoading] = useState(false);
//   const [error, setError] = useState(null);
//   const [setupIntent, setSetupIntent] = useState(null);
//   const [paymentMethod, setPaymentMethod] = useState('card');
//   const [savedMethods, setSavedMethods] = useState([]);
//   const [selectedSavedMethod, setSelectedSavedMethod] = useState(null);

//   const { plan, proratedAmount, isUpgrade, currentPlan, isDowngrade } =
//     location.state || {};

//   useEffect(() => {
//     if (!plan) {
//       navigate('/subscription/plans');
//       return;
//     }
//     fetchInitialData();
//   }, [plan]);

//   const fetchInitialData = async () => {
//     try {
//       // Fetch saved payment methods
//       const methodsResponse = await api.get('/subscriptions/payment-methods', {
//         headers: { Authorization: `Bearer ${token}` },
//       });
//       setSavedMethods(methodsResponse.data.methods);

//       // Create setup intent for new payment method
//       const setupResponse = await api.post(
//         '/subscriptions/payment-methods/setup-intent',
//         {},
//         {
//           headers: { Authorization: `Bearer ${token}` },
//         }
//       );
//       setSetupIntent(setupResponse.data.clientSecret);
//     } catch (error) {
//       console.error('Error initializing checkout:', error);
//       toast.error('Failed to initialize checkout');
//     }
//   };

//   const calculateTotalAmount = () => {
//     if (isDowngrade) {
//       return 0; // Downgrades take effect at end of billing period
//     }
//     if (isUpgrade) {
//       return proratedAmount || 0;
//     }
//     return plan.prices[currentPlan?.currency || 'usd'];
//   };

//   const handleSubmit = async (e) => {
//     e.preventDefault();
//     if (!stripe || !elements || loading) return;

//     setLoading(true);
//     setError(null);

//     try {
//       // If using a new payment method
//       if (!selectedSavedMethod) {
//         const { error: setupError, setupIntent: confirmedSetup } =
//           await stripe.confirmCardSetup(setupIntent, {
//             payment_method: {
//               card: elements.getElement(CardElement),
//               billing_details: { email },
//             },
//           });

//         if (setupError) {
//           throw setupError;
//         }

//         setSelectedSavedMethod(confirmedSetup.payment_method);
//       }

//       // Create or update subscription based on scenario
//       const endpoint = isUpgrade
//         ? '/subscriptions/upgrade'
//         : isDowngrade
//         ? '/subscriptions/downgrade'
//         : '/subscriptions/create';

//       const response = await api.post(
//         endpoint,
//         {
//           planId: plan.id,
//           paymentMethodId: selectedSavedMethod,
//           email,
//         },
//         {
//           headers: { Authorization: `Bearer ${token}` },
//         }
//       );

//       if (isDowngrade) {
//         toast.success(
//           `Your plan will be changed to ${plan.name} at the end of your billing period`
//         );
//       } else {
//         toast.success(
//           `Successfully ${isUpgrade ? 'upgraded' : 'subscribed to'} ${
//             plan.name
//           }`
//         );
//       }
//       navigate('/subscription/info');
//     } catch (error) {
//       console.error('Error processing subscription:', error);
//       setError(error.message);
//       toast.error('Failed to process subscription');
//     } finally {
//       setLoading(false);
//     }
//   };

//   if (!plan) return null;

//   return (
//     <div className='max-w-2xl mx-auto p-8'>
//       <h2 className='text-2xl font-bold mb-6'>
//         {isUpgrade
//           ? 'Upgrade Subscription'
//           : isDowngrade
//           ? 'Change Subscription Plan'
//           : 'Complete Purchase'}
//       </h2>

//       {/* Payment Summary */}
//       <div className='bg-gray-50 rounded-lg p-6 mb-8'>
//         <h3 className='text-lg font-semibold mb-4'>Payment Summary</h3>
//         <div className='space-y-2'>
//           <div className='flex justify-between'>
//             <span>{plan.name}</span>
//             <span>
//               {formatCurrency(
//                 plan.prices[currentPlan?.currency || 'usd'] / 100
//               )}
//               /month
//             </span>
//           </div>

//           {currentPlan && (
//             <div className='flex justify-between text-gray-600'>
//               <span>Current Plan</span>
//               <span>{PLAN_CONFIGS[currentPlan.planType].name}</span>
//             </div>
//           )}

//           {isUpgrade && proratedAmount > 0 && (
//             <div className='flex justify-between text-gray-600'>
//               <span>Prorated Adjustment</span>
//               <span>{formatCurrency(proratedAmount / 100)}</span>
//             </div>
//           )}

//           {isDowngrade && (
//             <div className='bg-yellow-50 p-4 rounded-md mt-4'>
//               <p className='text-sm text-yellow-800'>
//                 Your plan will change to {plan.name} at the end of your current
//                 billing period (
//                 {new Date(currentPlan.currentPeriodEnd).toLocaleDateString()}).
//                 No immediate payment is required.
//               </p>
//             </div>
//           )}

//           <div className='border-t pt-2 mt-2'>
//             <div className='flex justify-between font-semibold'>
//               <span>Total due today</span>
//               <span>{formatCurrency(calculateTotalAmount() / 100)}</span>
//             </div>
//           </div>
//         </div>
//       </div>

//       {/* Payment Method Selection */}
//       <form onSubmit={handleSubmit} className='space-y-6'>
//         {savedMethods.length > 0 && (
//           <div className='mb-8'>
//             <h3 className='text-lg font-semibold mb-4'>
//               Saved Payment Methods
//             </h3>
//             <div className='space-y-4'>
//               {savedMethods.map((method) => (
//                 <div
//                   key={method.id}
//                   className={`border rounded-lg p-4 cursor-pointer ${
//                     selectedSavedMethod === method.id
//                       ? 'border-blue-500 bg-blue-50'
//                       : ''
//                   }`}
//                   onClick={() => setSelectedSavedMethod(method.id)}
//                 >
//                   <div className='flex justify-between items-center'>
//                     <div>
//                       <p className='font-medium'>
//                         {method.card.brand.toUpperCase()} ••••{' '}
//                         {method.card.last4}
//                       </p>
//                       <p className='text-sm text-gray-600'>
//                         Expires {method.card.exp_month}/{method.card.exp_year}
//                       </p>
//                     </div>
//                   </div>
//                 </div>
//               ))}
//             </div>
//           </div>
//         )}

//         <div className='mb-6'>
//           <h3 className='text-lg font-semibold mb-4'>
//             {savedMethods.length > 0
//               ? 'Or Pay with New Card'
//               : 'Payment Method'}
//           </h3>

//           <div className='space-y-4'>
//             {/* <LinkAuthenticationElement
//               onChange={(e) => setEmail(e.value.email)}
//             /> */}

//             <div className='border rounded-lg p-4'>
//               <CardElement
//                 options={{
//                   style: {
//                     base: {
//                       fontSize: '16px',
//                       color: '#424770',
//                       '::placeholder': {
//                         color: '#aab7c4',
//                       },
//                     },
//                     invalid: {
//                       color: '#9e2146',
//                     },
//                   },
//                 }}
//               />
//             </div>
//           </div>
//         </div>

//         {error && <div className='text-red-500 text-sm mt-4'>{error}</div>}

//         <button
//           type='submit'
//           disabled={loading || !stripe || !elements}
//           className='w-full bg-blue-500 text-white py-3 px-6 rounded-lg hover:bg-blue-600
//                    disabled:opacity-50 disabled:cursor-not-allowed'
//         >
//           {loading ? (
//             <div className='flex items-center justify-center'>
//               {/* <LoadingSpinner className='h-5 w-5 mr-2' /> */}
//               Processing...
//             </div>
//           ) : isDowngrade ? (
//             'Confirm Plan Change'
//           ) : (
//             `Pay ${formatCurrency(calculateTotalAmount() / 100)}`
//           )}
//         </button>

//         <p className='text-center text-sm text-gray-600 mt-4'>
//           {isDowngrade
//             ? 'Your plan will change at the end of your current billing period'
//             : 'Your subscription will start immediately after successful payment'}
//         </p>
//       </form>
//     </div>
//   );
// };

// export default Checkout;

// // // src/components/subscription/Checkout.jsx
// import React, { useState, useEffect } from 'react';
// import { useLocation, useNavigate } from 'react-router-dom';
// import { toast } from 'react-toastify';
// import {
//   Elements,
//   PaymentElement,
//   LinkAuthenticationElement,
//   useStripe,
//   useElements,
// } from '@stripe/react-stripe-js';
// import { loadStripe } from '@stripe/stripe-js';
// import { PaymentMethodDialog } from './PaymentMethodDialog';
// import { formatCurrency } from '../../../utils/currency';
// import axios from 'axios';

// const api = axios.create({
//   baseURL: process.env.REACT_APP_API_URL,
//   timeout: 5000,
// });

// const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

// const CheckoutForm = ({ plan, proratedAmount, isUpgrade }) => {
//   const stripe = useStripe();
//   const elements = useElements();
//   const navigate = useNavigate();
//   const [email, setEmail] = useState('');
//   const [isLoading, setIsLoading] = useState(false);
//   const [message, setMessage] = useState(null);
//   const [paymentMethod, setPaymentMethod] = useState('card');
//   const [savedMethods, setSavedMethods] = useState({ cards: [], link: null });
//   const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);
//   const [showUpdateDialog, setShowUpdateDialog] = useState(false);

//   useEffect(() => {
//     fetchSavedPaymentMethods();
//   }, []);

//   const fetchSavedPaymentMethods = async () => {
//     try {
//       const token = localStorage.getItem('ownerToken');
//       const response = await api.get('/subscriptions/payment-methods', {
//         headers: { Authorization: `Bearer ${token}` },
//       });
//       setSavedMethods({
//         cards: response.data.cards || [],
//         link: response.data.link || null,
//         defaultMethod: response.data.defaultMethod || null,
//       });
//     } catch (error) {
//       console.error('Error fetching payment methods:', error);
//       toast.error('Failed to load saved payment methods');
//     }
//   };

//   const handleSubmit = async (e) => {
//     e.preventDefault();

//     if (!stripe || !elements) {
//       return;
//     }

//     setIsLoading(true);
//     setMessage(null);

//     try {
//       const { error } = await stripe.confirmPayment({
//         elements,
//         confirmParams: {
//           return_url: `${window.location.origin}/subscription/success`,
//           payment_method_data: {
//             billing_details: {
//               email: email,
//             },
//           },
//         },
//       });

//       if (error) {
//         setMessage(error.message);
//         toast.error(error.message);
//       }
//     } catch (error) {
//       console.error('Payment error:', error);
//       setMessage('An unexpected error occurred.');
//       toast.error('Payment failed. Please try again.');
//     } finally {
//       setIsLoading(false);
//     }
//   };

//   const handlePaymentMethodChange = (method) => {
//     setPaymentMethod(method);
//   };

//   return (
//     <div className='max-w-2xl mx-auto p-8'>
//       <h2 className='text-2xl font-bold mb-6'>Complete Purchase</h2>

//       {/* Payment Summary */}
//       <div className='bg-gray-50 rounded-lg p-6 mb-8'>
//         <h3 className='text-lg font-semibold mb-4'>Payment Summary</h3>
//         <div className='space-y-2'>
//           <div className='flex justify-between'>
//             <span>{plan.name}</span>
//             <span>{formatCurrency(plan.prices.usd / 100)}/month</span>
//           </div>
//           {isUpgrade && proratedAmount > 0 && (
//             <div className='flex justify-between text-gray-600'>
//               <span>Prorated adjustment</span>
//               <span>{formatCurrency(proratedAmount / 100)}</span>
//             </div>
//           )}
//           <div className='border-t pt-2 mt-2'>
//             <div className='flex justify-between font-semibold'>
//               <span>Total due today</span>
//               <span>
//                 {formatCurrency(
//                   (isUpgrade ? proratedAmount : plan.prices.usd) / 100
//                 )}
//               </span>
//             </div>
//           </div>
//         </div>
//       </div>

//       {/* Payment Method Selection */}
//       <div className='mb-8'>
//         <h3 className='text-lg font-semibold mb-4'>Payment Method</h3>
//         <div className='space-y-4'>
//           {savedMethods.link && (
//             <div className='border rounded-lg p-4'>
//               <div className='flex justify-between items-center'>
//                 <div className='flex-1'>
//                   <p className='font-medium'>Pay with Stripe Link</p>
//                   <p className='text-sm text-gray-600'>
//                     Connected as {savedMethods.link.email}
//                   </p>
//                 </div>
//                 <button
//                   onClick={() => {
//                     setSelectedPaymentMethod(savedMethods.link);
//                     setShowUpdateDialog(true);
//                   }}
//                   className='px-4 py-2 text-blue-500 hover:text-blue-700'
//                 >
//                   Update
//                 </button>
//                 <button
//                   onClick={() => handlePaymentMethodChange('link')}
//                   className={`ml-4 px-4 py-2 rounded-lg ${
//                     paymentMethod === 'link'
//                       ? 'bg-blue-500 text-white'
//                       : 'border border-gray-300'
//                   }`}
//                 >
//                   Select
//                 </button>
//               </div>
//             </div>
//           )}

//           <div className='border rounded-lg p-4'>
//             <div className='flex justify-between items-center'>
//               <div>
//                 <p className='font-medium'>Pay with card</p>
//                 <p className='text-sm text-gray-600'>Enter your card details</p>
//               </div>
//               <button
//                 onClick={() => handlePaymentMethodChange('card')}
//                 className={`px-4 py-2 rounded-lg ${
//                   paymentMethod === 'card'
//                     ? 'bg-blue-500 text-white'
//                     : 'border border-gray-300'
//                 }`}
//               >
//                 Select
//               </button>
//             </div>
//           </div>
//         </div>
//       </div>

//       {/* Payment Form */}
//       <form onSubmit={handleSubmit} className='space-y-6'>
//         <LinkAuthenticationElement onChange={(e) => setEmail(e.value.email)} />

//         <PaymentElement
//           options={{
//             defaultValues: {
//               billingDetails: {
//                 email: email,
//               },
//             },
//             layout: 'tabs',
//           }}
//         />

//         {message && <div className='text-red-500 text-sm mt-4'>{message}</div>}

//         <button
//           type='submit'
//           disabled={isLoading || !stripe || !elements}
//           className='w-full bg-blue-500 text-white py-3 px-6 rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed'
//         >
//           {isLoading ? (
//             <div className='flex items-center justify-center'>
//               <div className='animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2' />
//               Processing...
//             </div>
//           ) : (
//             `Pay ${formatCurrency(
//               (isUpgrade ? proratedAmount : plan.prices.usd) / 100
//             )}`
//           )}
//         </button>

//         <p className='text-center text-sm text-gray-600 mt-4'>
//           Your subscription will start immediately after successful payment
//         </p>
//       </form>

//       {/* Update Payment Method Dialog */}
//       {showUpdateDialog && selectedPaymentMethod && (
//         <PaymentMethodDialog
//           isOpen={showUpdateDialog}
//           onClose={() => setShowUpdateDialog(false)}
//           paymentMethod={selectedPaymentMethod}
//           onUpdate={() => {
//             fetchSavedPaymentMethods();
//             setShowUpdateDialog(false);
//           }}
//         />
//       )}
//     </div>
//   );
// };

// // const Checkout = () => {
// //   const location = useLocation();
// //   const navigate = useNavigate();
// //   const { plan, clientSecret, proratedAmount, isUpgrade } =
// //     location.state || {};

// //   useEffect(() => {
// //     if (!clientSecret || !plan) {
// //       navigate('/subscription/plans');
// //       return;
// //     }
// //   }, [clientSecret, plan, navigate]);

// //   if (!clientSecret || !plan) {
// //     return null;
// //   }

// //   return (
// //     <Elements
// //       stripe={stripePromise}
// //       options={{
// //         clientSecret,
// //         appearance: {
// //           theme: 'stripe',
// //         },
// //       }}
// //     >
// //       <CheckoutForm
// //         plan={plan}
// //         proratedAmount={proratedAmount}
// //         isUpgrade={isUpgrade}
// //       />
// //     </Elements>
// //   );
// // };

// // export default Checkout;

// const Checkout = () => {
//   const location = useLocation();
//   const navigate = useNavigate();
//   const {
//     plan,
//     clientSecret,
//     proratedAmount,
//     isUpgrade,
//     isDowngrade,
//     currentPlan,
//     action,
//   } = location.state || {};

//   const handleDowngradeConfirm = async () => {
//     try {
//       const token = localStorage.getItem('ownerToken');

//       await api.post(
//         '/subscriptions/downgrade',
//         {
//           planId: plan.id,
//         },
//         {
//           headers: { Authorization: `Bearer ${token}` },
//         }
//       );

//       toast.success(
//         `Your plan will be changed to ${plan.name} at the end of your billing period`
//       );
//       navigate('/subscription/info');
//     } catch (error) {
//       console.error('Error scheduling downgrade:', error);
//       toast.error('Failed to schedule downgrade');
//     }
//   };

//   // If it's a downgrade, show confirmation first
//   if (isDowngrade) {
//     return (
//       <div className='max-w-2xl mx-auto p-8'>
//         <h2 className='text-2xl font-bold mb-6'>Confirm Plan Change</h2>
//         <div className='bg-white rounded-lg shadow-md p-6'>
//           <div className='space-y-4'>
//             <p>
//               Your plan will be changed to {plan.name} at the end of your
//               current billing period (
//               {new Date(currentPlan?.currentPeriodEnd).toLocaleDateString()}).
//               You'll continue to have access to your current plan's features
//               until then.
//             </p>
//             <div className='bg-gray-50 p-4 rounded'>
//               <h4 className='font-semibold mb-2'>Payment Summary</h4>
//               <div className='flex justify-between'>
//                 <span>New Monthly Payment:</span>
//                 <span>
//                   {formatCurrency(plan.prices[currentPlan.currency] / 100)}
//                   /month
//                 </span>
//               </div>
//             </div>
//             <p className='text-sm text-gray-600'>
//               Your new rate will start on{' '}
//               {new Date(currentPlan?.currentPeriodEnd).toLocaleDateString()}.
//             </p>
//             <div className='flex justify-end space-x-4 mt-6'>
//               <button
//                 onClick={() => navigate('/subscription/plans')}
//                 className='px-4 py-2 text-gray-600 hover:text-gray-800'
//               >
//                 Cancel
//               </button>
//               <button
//                 onClick={handleDowngradeConfirm}
//                 className='px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600'
//               >
//                 Confirm Downgrade
//               </button>
//             </div>
//           </div>
//         </div>
//       </div>
//     );
//   }

//   // Regular checkout flow for new subscriptions and upgrades
//   return (
//     <Elements stripe={stripePromise} options={{ clientSecret }}>
//       <CheckoutForm
//         plan={plan}
//         proratedAmount={proratedAmount}
//         isUpgrade={isUpgrade}
//       />
//     </Elements>
//   );
// };

// export default Checkout;
